import { z } from 'zod';
import { vaiTroChiaSchema } from './chiaSe.js';
import { maKySchema } from './ky.js';
import { cursorQuerySchema } from './core.js';
/* ------------------------------------------------------------------ */
/* Hằng số                                                             */
/* ------------------------------------------------------------------ */
/** Thị trường chính. Danh sách mở, kênh mới có thể dùng mã khác. */
export const MARKETS = ['EN', 'PT', 'VI', 'ES', 'HI'];
export const MARKET_LABEL = {
    EN: 'EN',
    PT: 'PT',
    VI: 'VI',
    ES: 'ES',
    HI: 'HI',
};
export const marketSchema = z
    .string()
    .trim()
    .min(2, 'Mã thị trường quá ngắn')
    .max(8, 'Mã thị trường quá dài');
/**
 * Tình trạng kiếm tiền (YPP) — **nhập tay**.
 *
 * YouTube không có API trả tình trạng YPP cho chủ kênh: người quản lý xem trong
 * Studio rồi ghi vào app. App chỉ cảnh báo **gián tiếp** khi số liệu mâu thuẫn,
 * không bao giờ tự đổi giá trị này.
 */
export const MONETIZATION_STATUSES = [
    'not_eligible',
    'eligible',
    'under_review',
    'monetized',
    'rejected',
    'suspended',
];
export const monetizationStatusSchema = z.enum(MONETIZATION_STATUSES);
export const MONETIZATION_LABEL = {
    not_eligible: 'Chưa đủ điều kiện',
    eligible: 'Đủ điều kiện',
    under_review: 'Đang chờ xét duyệt',
    monetized: 'Đang kiếm tiền',
    rejected: 'Bị từ chối',
    suspended: 'Bị tắt kiếm tiền',
};
/** Tone của `StatusBadge` ứng với từng trạng thái kiếm tiền. */
export const MONETIZATION_TONE = {
    not_eligible: 'neutral',
    eligible: 'info',
    under_review: 'warn',
    monetized: 'ok',
    rejected: 'bad',
    suspended: 'bad',
};
/** Hai trạng thái "mất kiếm tiền" — có ngày bị từ chối/tắt và lý do. */
export const MONETIZATION_STATUSES_MAT = ['rejected', 'suspended'];
export function laTrangThaiMatKiemTien(t) {
    return MONETIZATION_STATUSES_MAT.includes(t);
}
export const MONETIZATION_DENY_REASONS = [
    'duplicate_content',
    'reused_content',
    'spam',
    'other',
];
export const monetizationDenyReasonSchema = z.enum(MONETIZATION_DENY_REASONS);
export const MONETIZATION_DENY_REASON_LABEL = {
    duplicate_content: 'Nội dung trùng lặp',
    reused_content: 'Nội dung dùng lại',
    spam: 'Spam / lừa đảo',
    other: 'Khác',
};
/** Báo trước bao nhiêu ngày khi tới hạn nộp đơn lại. */
export const HAN_NHAC_DANG_KY_LAI_NGAY = 7;
/**
 * Bao nhiêu ngày liên tiếp doanh thu bằng 0 thì nghi kênh đã bị tắt kiếm tiền.
 *
 * Bảy ngày: một kênh đang bật YPP mà cả tuần không ra đồng nào gần như chắc
 * chắn có chuyện, trong khi một hai ngày 0 đồng là bình thường (ngày lễ, số
 * liệu về chậm).
 */
export const NGAY_NGHI_TAT_KIEM_TIEN = 7;
export const HEALTH_STATUSES = ['ok', 'warn', 'bad'];
export const healthStatusSchema = z.enum(HEALTH_STATUSES);
export const HEALTH_LABEL = {
    ok: 'Bình thường',
    warn: 'Cần chú ý',
    bad: 'Có vấn đề',
};
export const HEALTH_TONE = {
    ok: 'ok',
    warn: 'warn',
    bad: 'bad',
};
export const ASSIGNMENT_ROLES = ['owner', 'member'];
export const assignmentRoleSchema = z.enum(ASSIGNMENT_ROLES);
export const ASSIGNMENT_ROLE_LABEL = {
    owner: 'Phụ trách chính',
    member: 'Hỗ trợ',
};
/**
 * Kênh có được tính là "đang kiếm tiền" trong kỳ hay không.
 *
 * Một luật duy nhất cho cả hệ thống: đã bật kiếm tiền **và** có doanh thu gộp
 * lớn hơn 0 trong kỳ. Nguồn dữ liệu không mang `monetizationStatus` (ví dụ dòng
 * doanh thu tháng của module 08) thì chỉ xét doanh thu.
 *
 * Dùng ở: `channels.thuocTab('unmonetized')`, thẻ "Tổng số kênh" của Tổng quan,
 * dòng "N kênh kiếm tiền" của trang Doanh thu. Số của hai trang có thể lệch nhau
 * vì **khác kỳ** (Tổng quan tính 7/28/90 ngày, Doanh thu tính theo tháng), không
 * phải vì khác luật.
 */
export function laKenhKiemTien(kenh) {
    if (kenh.monetizationStatus != null && kenh.monetizationStatus !== 'monetized')
        return false;
    const gop = Number(kenh.grossRevenue ?? 0);
    return Number.isFinite(gop) && gop > 0;
}
/* ------------------------------------------------------------------ */
/* Tình trạng kiếm tiền                                                */
/* ------------------------------------------------------------------ */
/**
 * Ô sửa tình trạng kiếm tiền ở tab Hồ sơ kênh. Chỉ `channels:admin` gọi được.
 *
 * Ba ràng buộc, kiểm ở cả Zod lẫn giao diện:
 *
 * 1. Trạng thái **không** mất kiếm tiền thì không được kèm ngày bị từ chối, lý
 *    do hay ngày đăng ký lại — để tránh dữ liệu cũ dính lại sau khi kênh bật lại.
 * 2. `rejected`/`suspended` **phải** có ngày bị từ chối/tắt: không có mốc đó thì
 *    không tính được hạn nộp đơn lại.
 * 3. Lý do `other` **phải** có ghi chú, nếu không dòng lịch sử vô nghĩa.
 */
export const monetizationInputSchema = z
    .object({
    status: monetizationStatusSchema,
    /// `YYYY-MM-DD`, giờ Việt Nam.
    deniedAt: z.iso.date().nullish(),
    reapplyAt: z.iso.date().nullish(),
    reason: monetizationDenyReasonSchema.nullish(),
    note: z.string().trim().max(1000).nullish(),
})
    .refine((d) => laTrangThaiMatKiemTien(d.status) || !d.deniedAt, {
    message: 'Chỉ trạng thái "Bị từ chối" hoặc "Bị tắt kiếm tiền" mới có ngày bị từ chối/tắt',
    path: ['deniedAt'],
})
    .refine((d) => laTrangThaiMatKiemTien(d.status) || !d.reason, {
    message: 'Chỉ trạng thái "Bị từ chối" hoặc "Bị tắt kiếm tiền" mới có lý do',
    path: ['reason'],
})
    .refine((d) => laTrangThaiMatKiemTien(d.status) || !d.reapplyAt, {
    message: 'Chỉ trạng thái "Bị từ chối" hoặc "Bị tắt kiếm tiền" mới có ngày được đăng ký lại',
    path: ['reapplyAt'],
})
    .refine((d) => !laTrangThaiMatKiemTien(d.status) || Boolean(d.deniedAt), {
    message: 'Nhập ngày bị từ chối hoặc bị tắt kiếm tiền',
    path: ['deniedAt'],
})
    .refine((d) => d.reason !== 'other' || Boolean(d.note?.trim()), {
    message: 'Lý do "Khác" thì phải ghi rõ trong ô ghi chú',
    path: ['note'],
})
    .refine((d) => !d.deniedAt || !d.reapplyAt || d.reapplyAt >= d.deniedAt, {
    message: 'Ngày được đăng ký lại phải sau ngày bị từ chối/tắt',
    path: ['reapplyAt'],
});
/** Tình trạng kiếm tiền hiện tại của một kênh. */
export const monetizationStateSchema = z.object({
    channelId: z.string(),
    status: monetizationStatusSchema,
    deniedAt: z.iso.date().nullable(),
    reapplyAt: z.iso.date().nullable(),
    reason: monetizationDenyReasonSchema.nullable(),
    note: z.string().nullable(),
    /// Số ngày còn lại tới hạn nộp đơn lại; âm là đã quá hạn, `null` khi không có hạn.
    reapplyInDays: z.number().int().nullable(),
});
/** Một dòng lịch sử đổi tình trạng. */
export const monetizationLogSchema = z.object({
    id: z.string(),
    fromStatus: monetizationStatusSchema.nullable(),
    toStatus: monetizationStatusSchema,
    deniedAt: z.iso.date().nullable(),
    reapplyAt: z.iso.date().nullable(),
    reason: monetizationDenyReasonSchema.nullable(),
    note: z.string().nullable(),
    /// Tên người đổi, `null` nếu tài khoản đó đã bị xoá.
    changedByName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const monetizationDetailSchema = z.object({
    state: monetizationStateSchema,
    logs: z.array(monetizationLogSchema),
});
/** Kỳ số liệu của màn hình quản lý kênh. */
export const PERIODS = ['7d', '28d', '90d', '365d', 'all'];
export const periodSchema = z.enum(PERIODS);
/**
 * Số ngày của mỗi kỳ.
 *
 * `all` không có số ngày cố định: phạm vi tính từ **ngày tạo kênh** (hoặc ngày
 * cũ nhất có số liệu). Giá trị ở đây là chặn trên hợp lý cho một kênh YouTube
 * — 20 năm, dài hơn tuổi của nền tảng.
 */
export const PERIOD_DAYS = {
    '7d': 7,
    '28d': 28,
    '90d': 90,
    '365d': 365,
    all: 7300,
};
export const PERIOD_LABEL = {
    '7d': '7 ngày qua',
    '28d': '28 ngày qua',
    '90d': '90 ngày qua',
    '365d': '365 ngày qua',
    all: 'Toàn thời gian',
};
/** Kỳ không so được với kỳ trước: `all` đã gồm mọi ngày có số liệu. */
export function coKyTruoc(period) {
    return period !== 'all';
}
/** Tab của màn hình danh sách kênh. */
/**
 * Tab của trang Quản lý kênh.
 *
 * `da-luu-tru` nằm **ngoài** bất biến `network + adsense + unmonetized = all`:
 * ba tab kia chia kênh đang hoạt động theo đường tiền, còn tab này là kênh đã
 * lưu trữ — vốn bị ẩn khỏi mọi danh sách và tổng kết.
 */
export const CHANNEL_TABS = ['all', 'network', 'adsense', 'unmonetized', 'da-luu-tru'];
export const CHANNEL_TAB_LABEL = {
    all: 'Tất cả',
    network: 'Qua Network',
    adsense: 'AdSense trực tiếp',
    unmonetized: 'Chưa kiếm tiền',
    'da-luu-tru': 'Đã lưu trữ',
};
export const CHANNEL_SORTS = [
    'revenue',
    'subscribers',
    /**
     * Lượt xem cao → thấp. Thêm 31/10/2026 làm **cách sắp mặc định của người
     * không có `revenue:view`**: mặc định cũ là `revenue`, mà bảng của họ không
     * còn cột đó (xem `loc-tien.ts`).
     */
    'views',
    'rpm',
    'name',
    'chia-nhan-su',
    'cong-ty-giu-lai',
    'phan-cua-toi',
];
export const CHANNEL_SORT_LABEL = {
    revenue: 'Doanh thu cao → thấp',
    subscribers: 'Subscribers cao → thấp',
    views: 'Lượt xem cao → thấp',
    rpm: 'RPM cao → thấp',
    name: 'Tên A → Z',
    /*
     * Ba cách sắp theo phần chia. Hai cách đầu chỉ có nghĩa với người xem đủ
     * (`chiaSe.chiaNhanSu` / `congTyGiu` không có trong JSON của người xem hẹp —
     * xem `channelChiaSeSchema`); chúng vẫn là mã hợp lệ để link cũ không 400,
     * và với người xem hẹp thì mọi dòng bằng 0 nên kết quả rơi về thứ tự tên.
     */
    'chia-nhan-su': 'Chia nhân sự cao → thấp',
    'cong-ty-giu-lai': 'Công ty giữ lại cao → thấp',
    'phan-cua-toi': 'Phần của bạn cao → thấp',
};
/* ------------------------------------------------------------------ */
/* Kênh                                                                */
/* ------------------------------------------------------------------ */
export const channelGroupSchema = z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    color: z.string(),
});
export const channelAssigneeSchema = z.object({
    userId: z.string(),
    name: z.string(),
    email: z.email(),
    avatarColor: z.string(),
    role: assignmentRoleSchema,
});
export const channelSummarySchema = z.object({
    id: z.string(),
    youtubeChannelId: z.string().nullable(),
    name: z.string(),
    handle: z.string(),
    avatarColor: z.string(),
    niche: z.string(),
    market: z.string(),
    language: z.string(),
    groupId: z.string().nullable(),
    group: channelGroupSchema.nullable(),
    monetizationStatus: monetizationStatusSchema,
    /**
     * Ba trường đi kèm tình trạng kiếm tiền, `null` khi kênh không ở trạng thái
     * mất kiếm tiền. Để ngay trong summary (không phải enricher) vì chúng là dữ
     * liệu của chính module 01 và danh sách kênh cần để hiện "tới hạn nộp lại".
     */
    monetizationDeniedAt: z.iso.date().nullable(),
    monetizationReapplyAt: z.iso.date().nullable(),
    monetizationDenyReason: monetizationDenyReasonSchema.nullable(),
    healthStatus: healthStatusSchema,
    healthNote: z.string().nullable(),
    createdAt: z.iso.datetime(),
    archivedAt: z.iso.datetime().nullable(),
    /** Có theo dõi kênh qua YouTube API hay không (màn hình Kết nối API). */
    apiTracked: z.boolean(),
    assignees: z.array(channelAssigneeSchema),
    /**
     * Chủ sở hữu chính của **Tài khoản thương hiệu**; `null` khi chưa khai.
     *
     * Nằm ở đây (không phải enricher) vì là dữ liệu của chính module 01, và bảng
     * danh sách kênh cần cột này để tra ngược quyền khi nhân sự nghỉ việc.
     */
    brandPrimaryOwnerEmail: z.string().nullable(),
    /** Lần soát quyền tài khoản thương hiệu gần nhất; `null` khi chưa soát. */
    brandLastVerifiedAt: z.string().nullable(),
    /**
     * Ngày kênh **bắt đầu vận hành** (ngày bắt đầu đăng video), khoá ngày
     * `YYYY-MM-DD`. Quản trị nhập tay; gợi ý sẵn ngày video đầu tiên nếu module
     * `sync` có số liệu.
     *
     * Chi phí phát sinh **trước** ngày này không trừ vào lợi nhuận kênh mà vào
     * mục "Đầu tư ban đầu" — xem `chiPhiTinhVaoVanHanh` trong `chiaSe.ts`.
     * `null` = chưa khai, khi đó **không** khoản chi nào bị trừ.
     */
    operationStartDate: z.iso.date().nullable(),
});
/* ------------------------------------------------------------------ */
/* Dữ liệu do enricher gắn thêm                                        */
/* ------------------------------------------------------------------ */
/** Số liệu YouTube của kỳ đang chọn. Do module `sync`/`analytics` cung cấp. */
export const channelMetricsSchema = z.object({
    subscribers: z.number().int().nonnegative(),
    /**
     * Lượt xem trong kỳ đang chọn (xem `period` của truy vấn).
     *
     * `null` = **chưa có số liệu lượt xem** cho kỳ này. Không bao giờ dùng `0`
     * thay cho "không biết": `0` làm mẫu số RPM sai và làm cột Xem nói dối.
     */
    views: z.number().int().nonnegative().nullable(),
    /** Số ngày trong kỳ thật sự có lượt xem. */
    soNgayCoViews: z.number().int().nonnegative(),
    /** Số ngày của kỳ, để giao diện ghi "chỉ tính N/28 ngày". */
    soNgayTrongKy: z.number().int().nonnegative(),
});
/** Nơi trả tiền cho kênh. Do module `networks` cung cấp. */
export const channelPaymentSchema = z.object({
    partnerId: z.string(),
    partnerName: z.string(),
    kind: z.enum(['network', 'adsense']),
    /**
     * Ví dụ "Công ty 85% · Network 15%". `null` khi nhận trực tiếp qua AdSense.
     *
     * **Tuỳ chọn**: đây là tỷ lệ chia tiền với Network, nên thiếu `revenue:view`
     * thì máy chủ bỏ hẳn (xem `loc-tien.ts`). `partnerName` và `kind` thì giữ —
     * "kênh này đi Network nào" là một việc vận hành, không phải một con số tiền.
     */
    splitLabel: z.string().nullish(),
    /** Trạng thái liên kết AdSense, làm thủ công ngoài app. */
    linkStatus: z.enum(['pending', 'linked']).nullable(),
});
/** Doanh thu của kỳ đang chọn. Do module `revenue` cung cấp. Tiền là chuỗi Decimal. */
export const channelRevenueSummarySchema = z.object({
    grossRevenue: z.string(),
    /**
     * Thuế Mỹ Google giữ lại, trừ **trước** phần Network.
     *
     * Cùng `vat` bên dưới, hai cột này đã có trong `revenueSummaryRowSchema` của
     * module 08 từ đầu nhưng enricher không chuyển tiếp, nên không màn nào ngoài
     * trang Doanh thu (kỳ tháng) đọc được hai khoản trừ đầu chuỗi. Khối "Dòng
     * tiền" của Tổng quan cần đủ chuỗi để thanh xếp chồng cộng lại đúng bằng
     * doanh thu gộp — xem `docs/ui.md` mục 17.
     */
    thueMy: z.string(),
    networkShare: z.string(),
    /** VAT trên phần công ty nhận, trừ **sau** phần Network. */
    vat: z.string(),
    companyNet: z.string(),
    /**
     * RPM của kỳ, `null` khi **chưa đủ số liệu lượt xem**.
     *
     * Chỉ tính trên những ngày có **cả** doanh thu và lượt xem > 0. Kỳ không có
     * ngày nào như vậy thì trả `null` để giao diện hiện `—`, chứ không trả
     * `'0.00'` (đọc ra "RPM bằng 0") hay chia cho một mẫu số thiếu ngày (kênh
     * Dreamy Cat TV 16/10/2026: 99,49 / 160 × 1000 = 621,81 $).
     */
    rpm: z.string().nullable(),
    /** Lượt xem đã dùng làm mẫu số RPM, để giao diện giải thích con số. */
    rpmViews: z.number().int().nonnegative(),
    /** Số ngày đã vào phép tính RPM. `0` ⇒ `rpm = null`. */
    rpmSoNgay: z.number().int().nonnegative(),
    currency: z.string().default('USD'),
    /** Phần trăm thay đổi so với kỳ trước, `null` khi chưa đủ dữ liệu. */
    deltaPercent: z.number().nullable(),
});
/** Một người trong đội ngũ kênh, dạng pill "tên · %" trên bảng danh sách. */
export const channelNguoiChiaSchema = z.object({
    userId: z.string(),
    tenNguoi: z.string(),
    avatarColor: z.string(),
    /**
     * Tổng % của người này ở vai trò đó; 0 khi chỉ hưởng phần cố định.
     *
     * Ba số dưới đây **tuỳ chọn**: thiếu `revenue:view` thì máy chủ bỏ hẳn chúng
     * (xem `loc-tien.ts`), pill còn lại đúng cái tên.
     */
    pct: z.number().optional(),
    /** Tổng phần cố định USD/tháng. */
    coDinhUsd: z.number().optional(),
    /** Tiền thực chia của kỳ đang chọn. */
    tien: z.number().optional(),
});
/** Trạng thái kết nối API. Do module `sync` cung cấp. */
export const channelSyncStatusSchema = z.object({
    connected: z.boolean(),
    lastSyncAt: z.iso.datetime().nullable(),
    message: z.string().nullable(),
    /**
     * Tiến độ số ngày có số liệu của kênh (module 03 gắn vào).
     *
     * Bảng Quản lý kênh cần nó để hiện "Đang lấy lịch sử · x/120 ngày" và chú
     * thích "chưa đủ dữ liệu" khi kỳ đang xem thiếu ngày.
     */
    lichSu: z
        .object({
        soNgayCan: z.number().int().positive(),
        soNgayDaCo: z.number().int().nonnegative(),
        dangChay: z.boolean(),
    })
        .nullable(),
});
/**
 * Nhóm, đội ngũ và phần chia của kỳ đang chọn. Do module `team` cung cấp.
 *
 * Tiền ở đây là **số** (không phải chuỗi Decimal như `revenueSummary`): cả bốn
 * số đều do `tinhChiaKenh` tính trên cent rồi trả về hai số lẻ, không đọc thẳng
 * từ cột `Decimal(14,4)` nào.
 *
 * ## Che theo người xem (`daChe`)
 *
 * Người **không** được xem phần chia đầy đủ (xem
 * `api/lib/chiaDayDu.duocXemChiaDayDu`) nhận bản đã che: ba mảng pill chỉ còn
 * dòng của chính họ, `chiaNhanSu` và `congTyGiu` **không có trong JSON**, và
 * `phanCuaToi` / `pctCuaToi` nói phần của riêng họ.
 *
 * Trước đợt này enricher `chiaSe` không che gì, nên Quản lý kênh mở bảng Quản
 * lý kênh ở cách xem "theo nhóm" / "theo người" là thấy tên, % và tiền của mọi
 * người trong công ty.
 */
export const channelChiaSeSchema = z.object({
    teamId: z.string().nullable(),
    tenNhom: z.string().nullable(),
    /** Pill tên · % cho ba vai trò, đã gộp các dòng của cùng một người. */
    truongNhom: z.array(channelNguoiChiaSchema),
    quanLy: z.array(channelNguoiChiaSchema),
    editor: z.array(channelNguoiChiaSchema),
    /*
     * Mọi số dưới đây **tuỳ chọn**: thiếu `revenue:view` thì máy chủ bỏ hẳn
     * chúng và chỉ giữ phần cấu trúc (nhóm, tên người, vai trò) — xem
     * `loc-tien.ts`. Khác `chiaNhanSu`/`congTyGiu`: hai cái đó tuỳ chọn theo
     * **người xem đủ phần chia**, một luật riêng và có từ trước.
     */
    thucNhan: z.number().optional(),
    chiPhi: z.number().optional(),
    loiNhuan: z.number().optional(),
    /** **Chỉ người xem đủ.** */
    chiaNhanSu: z.number().optional(),
    /** **Chỉ người xem đủ.** */
    congTyGiu: z.number().optional(),
    /** `true` khi dữ liệu đã bị che theo người xem. */
    daChe: z.boolean(),
    /** Phần của **người đang xem** ở kênh này; `null` = không hưởng % ⇒ hiện `—`. */
    phanCuaToi: z.number().nullish(),
    /** Tỷ lệ của người đang xem; `null` khi không có dòng, đổi giữa kỳ, hay khoán. */
    pctCuaToi: z.number().nullish(),
    /** Vai trò của người đang xem ở kênh này, theo thứ tự `VAI_TRO_CHIA`. */
    vaiTroCuaToi: z.array(vaiTroChiaSchema),
    /**
     * `true` khi còn ít nhất một dòng chia của kênh **tính tạm** (kỳ chưa chốt ở
     * trang Đối soát nhân sự). `false` = mọi dòng lấy từ ảnh chụp lúc chốt, tức
     * số đã đem đi trả tiền — giao diện khi đó **bỏ** chip "tạm tính"
     * (`docs/ui.md` mục 16).
     */
    tamTinh: z.boolean(),
    /** `true` khi lợi nhuận ≤ 0 — bảng ghi "Kênh lỗ — chia theo thực nhận". */
    kenhLo: z.boolean().optional(),
    chiaTheoThucNhan: z.boolean().optional(),
    /**
     * `true` khi kênh chưa khai `operationStartDate`: chi phí vẫn trừ đủ nhưng
     * khoản tiêu trước lúc kênh đăng video chưa được tách. Bảng ghi nhãn để không
     * ai đem con số đó đi trả tiền.
     */
    thieuNgayVanHanh: z.boolean(),
    /** Tổng % của kênh, để cảnh báo vượt ngưỡng ngay trên danh sách. */
    tongPct: z.number().optional(),
    canhBaoVuotPct: z.boolean().optional(),
});
/** Một dòng trong bảng quản lý kênh: dữ liệu gốc + phần do enricher gắn thêm. */
export const channelRowSchema = channelSummarySchema.extend({
    metrics: channelMetricsSchema.nullable(),
    payment: channelPaymentSchema.nullable(),
    /** Bỏ hẳn khi người xem không có `revenue:view` — xem `loc-tien.ts`. */
    revenueSummary: channelRevenueSummarySchema.nullish(),
    syncStatus: channelSyncStatusSchema.nullable(),
    chiaSe: channelChiaSeSchema.nullable(),
});
/** Năm trường mà enricher được phép gắn thêm vào mỗi dòng. */
export const CHANNEL_ENRICHED_FIELDS = [
    'metrics',
    'payment',
    'revenueSummary',
    'syncStatus',
    'chiaSe',
];
/* ------------------------------------------------------------------ */
/* Danh sách và tổng hợp                                               */
/* ------------------------------------------------------------------ */
export const listChannelsQuerySchema = cursorQuerySchema.extend({
    q: z.string().trim().optional(),
    market: z.string().trim().optional(),
    niche: z.string().trim().optional(),
    groupId: z.string().optional(),
    ownerId: z.string().optional(),
    monetizationStatus: monetizationStatusSchema.optional(),
    /** Lọc theo đối tác thanh toán, dữ liệu lấy từ enricher `payment`. */
    paymentPartnerId: z.string().optional(),
    /**
     * Lọc theo **nhóm** (`teams` của module `team`) — khác `groupId` là nhóm
     * **chủ đề** của chính module này. `khong-nhom` lấy các kênh chưa gán nhóm.
     */
    teamId: z.string().optional(),
    /** Lọc theo người có mặt trong đội ngũ kênh (bất kể vai trò). */
    nguoiId: z.string().optional(),
    /**
     * Lọc theo vai trò; chỉ có nghĩa khi đi cùng `nguoiId`. Nhận **một hoặc
     * nhiều** vai trò, phân cách bằng dấu phẩy trên query string.
     *
     * Nhiều vai trò là để chip "Kênh tôi quản lý" của trang Hiệu suất lấy được
     * `quan_ly` **và** `truong_nhom` trong một lượt: hai lượt gọi rồi hợp lại ở
     * chỗ gọi là hai lần chạy cả chuỗi enricher cho cùng một câu hỏi.
     */
    vaiTroChia: z
        .union([z.string(), z.array(z.string())])
        .optional()
        .transform((v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v))
        .pipe(z
        .array(z.enum(['truong_nhom', 'quan_ly', 'editor']))
        .min(1)
        .optional()),
    /**
     * Tìm mọi kênh mà email này còn quyền trên Tài khoản thương hiệu — khớp
     * `primaryOwnerEmail`, `owners` **hoặc** `managers`.
     *
     * Dùng khi nhân sự nghỉ việc: một lượt tìm ra hết chỗ phải thu hồi quyền.
     */
    emailQuyen: z.string().trim().toLowerCase().optional(),
    tab: z.enum(CHANNEL_TABS).default('all'),
    sort: z.enum(CHANNEL_SORTS).default('revenue'),
    /**
     * **Mã kỳ** của cả app (`@qlk/shared/ky.ts`), không còn giới hạn ở năm
     * preset: nhận thêm `YYYY`, `YYYY-MM` và `tu-…_den-…`. Năm preset cũ
     * (`7d|28d|90d|365d|all`) vẫn là mã hợp lệ nên link cũ không hỏng.
     */
    period: maKySchema.default('28d'),
    /** Bí danh của `period` — tên tham số dùng chung với mọi endpoint khác. */
    ky: maKySchema.optional(),
    includeArchived: z.stringbool().default(false),
});
/** Dòng tổng của bảng. Tiền là chuỗi Decimal. */
export const channelTotalsSchema = z.object({
    channels: z.number().int().nonnegative(),
    subscribers: z.number().int().nonnegative(),
    /// `null` khi không kênh nào trong bảng có số liệu lượt xem.
    views: z.number().int().nonnegative().nullable(),
    /* Năm trường tiền — bỏ hẳn khi thiếu `revenue:view` (xem `loc-tien.ts`). */
    grossRevenue: z.string().optional(),
    networkShare: z.string().optional(),
    companyNet: z.string().optional(),
    /// `null` khi chưa đủ số liệu lượt xem — xem `channelRevenueSummarySchema.rpm`.
    avgRpm: z.string().nullish(),
    currency: z.string().optional(),
});
export const listChannelsResponseSchema = z.object({
    items: z.array(channelRowSchema),
    nextCursor: z.string().nullable(),
    /** Tổng của toàn bộ tập đã lọc, không chỉ trang hiện tại. */
    totals: channelTotalsSchema,
});
export const channelsSummaryQuerySchema = z
    .object({
    /** Mã kỳ của cả app — xem `listChannelsQuerySchema.period`. */
    period: maKySchema.default('28d'),
    /** Bí danh của `period` — tên tham số dùng chung với mọi endpoint khác. */
    ky: maKySchema.optional(),
})
    .transform((q) => ({ period: q.ky ?? q.period }));
/**
 * Kết quả lưu trữ kênh: ngoài kênh đã đổi còn kèm việc các module khác đã làm
 * (thu hồi token, huỷ bài lịch chưa upload) và việc không làm được.
 */
export const ketQuaLuuTruSchema = z.object({
    kenh: channelSummarySchema,
    daLam: z.array(z.object({ ten: z.string(), moTa: z.string() })),
    khongLamDuoc: z.array(z.object({ ten: z.string(), lyDo: z.string() })),
});
/** Đếm trước những gì mất khi xoá vĩnh viễn một kênh. */
export const xemTruocXoaKenhSchema = z.object({
    channelId: z.string(),
    name: z.string(),
    /** Kênh phải đã lưu trữ mới cho xoá. */
    daLuuTru: z.boolean(),
    /** Số dòng sẽ xoá theo từng loại dữ liệu. */
    soDong: z.record(z.string(), z.number().int().nonnegative()),
    /** Lý do chặn xoá, ví dụ sao kê Network đã qua bước đối soát. */
    chanBoi: z.array(z.string()),
    xoaDuoc: z.boolean(),
});
/**
 * Xoá vĩnh viễn: phải gõ lại **đúng tên kênh**. Máy chủ kiểm lại, không tin
 * giao diện.
 */
export const xoaKenhInputSchema = z.object({
    tenXacNhan: z.string().min(1, 'Phải gõ lại tên kênh'),
});
export const channelsSummarySchema = z.object({
    /** Mã kỳ đã dùng để tính (có thể là tháng, năm hay khoảng tự chọn). */
    period: z.string(),
    /** Nhãn kỳ để in trên nhãn ô thống kê: `28 ngày qua`, `Tháng 9/2026`. */
    nhanKy: z.string(),
    /**
     * Khoảng ngày **thực tế** của kỳ (`YYYY-MM-DD`), kết thúc ở ngày gần nhất đã
     * có số liệu. Giao diện ghi dưới bộ chọn kỳ để người dùng đối chiếu được với
     * YouTube Studio.
     */
    from: z.string(),
    to: z.string(),
    totals: channelTotalsSchema,
    /** Phần trăm doanh thu gộp mà công ty thực nhận. */
    netPercent: z.number().optional(),
    /** Số network đang trả tiền. */
    payingPartners: z.number().int().nonnegative(),
    /** Thay đổi doanh thu gộp so với kỳ trước. */
    grossDeltaPercent: z.number().nullish(),
    topRpm: z.object({ channelId: z.string(), name: z.string(), rpm: z.string() }).nullish(),
    counts: z.object({
        all: z.number().int().nonnegative(),
        network: z.number().int().nonnegative(),
        adsense: z.number().int().nonnegative(),
        unmonetized: z.number().int().nonnegative(),
        /**
         * Số kênh **đã lưu trữ**. Ngoài bất biến tổng ba tab kia: `all` chỉ đếm
         * kênh đang hoạt động.
         */
        'da-luu-tru': z.number().int().nonnegative(),
    }),
    /**
     * Tỷ trọng doanh thu từng kênh, đã sắp từ cao xuống thấp.
     *
     * **Bỏ hẳn** khi thiếu `revenue:view`: không chỉ vì có `grossRevenue`, mà vì
     * **thứ tự** của danh sách cũng là một con số tiền (ai thu nhiều hơn ai).
     */
    revenueShare: z
        .array(z.object({
        channelId: z.string(),
        name: z.string(),
        avatarColor: z.string(),
        grossRevenue: z.string(),
        percent: z.number(),
    }))
        .optional(),
    channelsWithoutRevenue: z.array(z.object({
        channelId: z.string(),
        name: z.string(),
        avatarColor: z.string(),
        niche: z.string(),
        monetizationStatus: monetizationStatusSchema,
    })),
});
/* ------------------------------------------------------------------ */
/* Input                                                               */
/* ------------------------------------------------------------------ */
const tenKenh = z.string().trim().min(2, 'Tên kênh quá ngắn').max(120, 'Tên kênh quá dài');
const handleKenh = z
    .string()
    .trim()
    .min(2, 'Handle quá ngắn')
    .max(60)
    .regex(/^@?[\p{L}\p{N}._-]+$/u, 'Handle chỉ gồm chữ, số và . _ -');
export const createChannelInputSchema = z.object({
    name: tenKenh,
    handle: handleKenh,
    niche: z.string().trim().min(1, 'Chưa nhập chủ đề').max(80),
    market: marketSchema,
    language: z.string().trim().min(2).max(40),
    groupId: z.string().nullish(),
    youtubeChannelId: z.string().trim().min(1).nullish(),
    avatarColor: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Màu phải dạng #RRGGBB')
        .optional(),
    monetizationStatus: monetizationStatusSchema.default('not_eligible'),
    healthStatus: healthStatusSchema.default('ok'),
    healthNote: z.string().trim().max(300).nullish(),
    apiTracked: z.boolean().optional(),
});
export const updateChannelInputSchema = createChannelInputSchema.partial();
export const channelProfileSchema = z.object({
    channelId: z.string(),
    /** Email tài khoản quản lý, đã che: `abc••••@gmail.com`. */
    managingAccountLabel: z.string().nullable(),
    titleTemplate: z.string().nullable(),
    descriptionTemplate: z.string().nullable(),
    tags: z.array(z.string()),
    notes: z.string().nullable(),
});
export const channelProfileInputSchema = z.object({
    managingAccountLabel: z.string().trim().max(120).nullish(),
    titleTemplate: z.string().trim().max(300).nullish(),
    descriptionTemplate: z.string().trim().max(5000).nullish(),
    tags: z.array(z.string().trim().min(1).max(40)).max(30).default([]),
    notes: z.string().trim().max(5000).nullish(),
});
export const channelGroupInputSchema = z.object({
    name: z.string().trim().min(2, 'Tên nhóm quá ngắn').max(80),
    color: z
        .string()
        .regex(/^#[0-9A-Fa-f]{6}$/, 'Màu phải dạng #RRGGBB')
        .optional(),
});
export const assignmentsInputSchema = z.object({
    assignees: z
        .array(z.object({ userId: z.string().min(1), role: assignmentRoleSchema.default('member') }))
        .max(20),
});
/** Một kênh được giao cho một người, nhìn từ phía người dùng (module team). */
export const userChannelAssignmentSchema = z.object({
    channelId: z.string(),
    channelName: z.string(),
    avatarColor: z.string(),
    role: assignmentRoleSchema,
    assignedAt: z.iso.datetime(),
});
/** Dòng tra cứu kênh, dùng khi khớp dòng sao kê của module networks. */
export const channelLookupRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    handle: z.string(),
    youtubeChannelId: z.string().nullable(),
    /** Module sync lọc theo cờ này để biết kênh nào thuộc danh sách API. */
    apiTracked: z.boolean(),
    /** Tình trạng kiếm tiền đang khai, để module 08 đếm đúng "N kênh kiếm tiền". */
    monetizationStatus: monetizationStatusSchema,
});
/** Người có thể nhận phụ trách kênh. Module `team` sẽ cung cấp endpoint thay thế. */
export const nguoiPhuTrachSchema = z.object({
    id: z.string(),
    name: z.string(),
    email: z.email(),
    avatarColor: z.string(),
});
/* ------------------------------------------------------------------ */
/* Tài khoản thương hiệu (Brand Account)                               */
/* ------------------------------------------------------------------ */
/**
 * Ai đang giữ quyền trên **Tài khoản thương hiệu** của kênh.
 *
 * Dùng khi nhân sự nghỉ việc: tra ngược một email ra mọi kênh người đó còn
 * quyền. Vì vậy `owners` và `managers` là danh sách email, và danh sách kênh có
 * bộ lọc tìm theo email bất kỳ trong ba trường đó.
 *
 * ⛔ **Chỉ lưu email.** Không có trường nào cho mật khẩu, mã khôi phục hay mã
 * 2FA — và test `khongCoTruongMatKhau` khoá luật đó lại.
 *
 * Ba trường `identityEmail`, `grantedByEmail`, `youtubeTitle`/`youtubeCustomUrl`
 * **không lưu ở bảng này**: chúng đọc từ kết nối OAuth của module 03 nên không
 * nhân bản dữ liệu, và giao diện gắn nhãn "từ Google".
 */
export const brandAccountSchema = z.object({
    channelId: z.string(),
    /* --- Nhập tay --- */
    /** Tên tài khoản thương hiệu như Google hiện ở màn chuyển tài khoản, ví dụ `S9gQr`. */
    brandAccountName: z.string().nullable(),
    /** Mã trong link `myaccount.google.com/brandaccounts/<id>`. */
    brandAccountId: z.string().nullable(),
    primaryOwnerEmail: z.string().nullable(),
    owners: z.array(z.string()),
    managers: z.array(z.string()),
    note: z.string().nullable(),
    /* --- Từ Google, chỉ đọc --- */
    /**
     * Email tài khoản đã đồng ý OAuth. Với Brand Account, Google trả địa chỉ nội
     * bộ dạng `…@pages.plusgoogle.com` — **không** phải email người thật.
     */
    identityEmail: z.string().nullable(),
    /**
     * Email của lượt cấp quyền gần nhất (OAuth hoặc link mời).
     *
     * Cùng hạn chế như `identityEmail`: khi người ta đồng ý dưới danh nghĩa Brand
     * Account thì Google chỉ trả địa chỉ của chính Brand Account đó. Muốn biết
     * **người** nào bấm thì ghi vào `owners`/`note`.
     */
    grantedByEmail: z.string().nullable(),
    /** Tên và handle kênh phía YouTube, do job `stats` của module 03 cập nhật. */
    youtubeTitle: z.string().nullable(),
    youtubeCustomUrl: z.string().nullable(),
    /* --- Kiểm tra định kỳ --- */
    lastVerifiedAt: z.string().nullable(),
    lastVerifiedByName: z.string().nullable(),
    /* --- Cờ cảnh báo, máy chủ tính --- */
    /** Chưa kiểm tra lần nào, hoặc lần cuối đã quá `NGAY_NHAC_KIEM_TRA_THUONG_HIEU`. */
    canKiemTraLai: z.boolean(),
    /** Chưa khai chủ sở hữu chính. */
    thieuChuSoHuu: z.boolean(),
    /** Tên kênh trong app khác tên phía YouTube. */
    lechTen: z.boolean(),
    /** Handle trong app khác `customUrl` phía YouTube (không phân biệt hoa thường và dấu `@`). */
    lechHandle: z.boolean(),
});
/** Quá bao nhiêu ngày thì nhắc kiểm tra lại danh sách quyền. */
export const NGAY_NHAC_KIEM_TRA_THUONG_HIEU = 90;
/** Email chuẩn hoá về chữ thường để so sánh và tra ngược được. */
const emailThuongHieuSchema = z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email('Email không hợp lệ').max(160));
/**
 * Tách mã tài khoản thương hiệu từ **link dán vào**.
 *
 * Người dùng dán cả URL `https://myaccount.google.com/brandaccounts/123…` nên
 * nhận luôn dạng đó rồi tự lấy phần số; gõ tay mã trần cũng được.
 */
export function tachMaThuongHieu(x) {
    const sach = x.trim();
    if (sach === '')
        return null;
    const khop = /brandaccounts\/(\d{6,})/.exec(sach);
    if (khop?.[1])
        return khop[1];
    return /^\d{6,}$/.test(sach) ? sach : null;
}
export const brandAccountInputSchema = z.object({
    brandAccountName: z.string().trim().max(120).nullish(),
    /**
     * Nhận cả link `myaccount.google.com/brandaccounts/<id>` lẫn mã trần; chuỗi
     * không chứa mã nào thì báo lỗi thay vì lưu rác.
     */
    brandAccountId: z
        .string()
        .trim()
        .nullish()
        .transform((x, ctx) => {
        if (x == null || x === '')
            return null;
        const ma = tachMaThuongHieu(x);
        if (ma === null) {
            ctx.addIssue({
                code: 'custom',
                message: 'Dán link myaccount.google.com/brandaccounts/<mã>, hoặc gõ mã (chỉ chữ số)',
            });
            return null;
        }
        return ma;
    }),
    primaryOwnerEmail: emailThuongHieuSchema.nullish(),
    owners: z.array(emailThuongHieuSchema).max(50).default([]),
    managers: z.array(emailThuongHieuSchema).max(50).default([]),
    note: z.string().trim().max(2000).nullish(),
});
//# sourceMappingURL=channels.js.map